import { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import {
    pesanLintasBulanDialog, pesanTanggalRsJejaring, pesanUrutanTanggal, teksRentangIso, teksTanggalSegmen,
} from "@/lib/rotasiRsJejaring";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Pencil, Trash2, Building2 } from "lucide-react";
import { toast } from "sonner";

const BULAN_NAMA = [
    "", "Januari", "Februari", "Maret", "April", "Mei", "Juni",
    "Juli", "Agustus", "September", "Oktober", "November", "Desember",
];

const now = new Date();
const EMPTY_FORM = {
    bulan: String(now.getMonth() + 1),
    tahun: String(now.getFullYear()),
    departemen_id: "",
    divisi_id: "",
    instalasi_stase_id: "",
    tanggal_mulai_stase: "",
    tanggal_akhir_stase: "",
    instalasi_jaga_id: "",
    tanggal_mulai_jaga: "",
    tanggal_akhir_jaga: "",
    is_rs_jejaring: false,
    nama_rs_jejaring: "",
    tanggal_mulai_rs_jejaring: "",
    tanggal_akhir_rs_jejaring: "",
};

// Tab "Rotasi Klinik" di dialog Edit Peserta Didik -- daftar segmen rotasi klinis
// bulanan peserta ybs, dikelompokkan per Bulan. Menggantikan Form Rotasi Klinik
// manual di Google Sheets. Murni monitoring, TIDAK terhubung ke Unit Cost.
export default function RotasiKlinikTab({ pesertaId, pesertaNama, locked }) {
    const [rotasiList, setRotasiList] = useState([]);
    const [departemenList, setDepartemenList] = useState([]);
    const [divisiList, setDivisiList] = useState([]);
    const [instalasiList, setInstalasiList] = useState([]);
    const [loading, setLoading] = useState(false);

    const [dialogOpen, setDialogOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(EMPTY_FORM);
    const [saving, setSaving] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(null);
    // Aturan lintas bulan dari server (sama dgn panel) utk pemeriksaan awal sebelum simpan; null = server yg menilai.
    const [aturan, setAturan] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/rotasi-klinik", { params: { peserta_id: pesertaId } });
            setRotasiList(data);
        } catch {
            toast.error("Gagal memuat data rotasi klinik");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        (async () => {
            try {
                const [dep, div, inst] = await Promise.all([
                    api.get("/departemen-klinik"),
                    api.get("/divisi-klinik"),
                    api.get("/instalasi-klinik"),
                ]);
                setDepartemenList(dep.data);
                setDivisiList(div.data);
                setInstalasiList(inst.data);
            } catch {
                toast.error("Gagal memuat master data Departemen/Divisi/Instalasi");
            }
        })();
        api.get("/rotasi-klinik/aturan").then(({ data }) => setAturan(data)).catch(() => setAturan(null));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pesertaId]);

    const departemenMap = useMemo(() => {
        const m = {};
        departemenList.forEach((d) => (m[d.id] = d));
        return m;
    }, [departemenList]);
    const divisiMap = useMemo(() => {
        const m = {};
        divisiList.forEach((d) => (m[d.id] = d));
        return m;
    }, [divisiList]);
    const instalasiMap = useMemo(() => {
        const m = {};
        instalasiList.forEach((d) => (m[d.id] = d));
        return m;
    }, [instalasiList]);
    const divisiForDepartemen = useMemo(
        () => divisiList.filter((d) => d.departemen_id === form.departemen_id),
        [divisiList, form.departemen_id]
    );

    const grouped = useMemo(() => {
        const map = new Map();
        rotasiList.forEach((r) => {
            const key = `${r.tahun}-${String(r.bulan).padStart(2, "0")}`;
            if (!map.has(key)) map.set(key, { bulan: r.bulan, tahun: r.tahun, items: [] });
            map.get(key).items.push(r);
        });
        return [...map.values()].sort((a, b) => (b.tahun - a.tahun) || (b.bulan - a.bulan));
    }, [rotasiList]);

    const openAdd = () => {
        setEditing(null);
        setForm(EMPTY_FORM);
        setDialogOpen(true);
    };

    const openEdit = (r) => {
        setEditing(r);
        setForm({
            bulan: String(r.bulan), tahun: String(r.tahun),
            departemen_id: r.departemen_id || "", divisi_id: r.divisi_id || "",
            instalasi_stase_id: r.instalasi_stase_id || "",
            tanggal_mulai_stase: r.tanggal_mulai_stase || "", tanggal_akhir_stase: r.tanggal_akhir_stase || "",
            instalasi_jaga_id: r.instalasi_jaga_id || "",
            tanggal_mulai_jaga: r.tanggal_mulai_jaga || "", tanggal_akhir_jaga: r.tanggal_akhir_jaga || "",
            is_rs_jejaring: !!r.is_rs_jejaring, nama_rs_jejaring: r.nama_rs_jejaring || "",
            tanggal_mulai_rs_jejaring: r.tanggal_mulai_rs_jejaring || "",
            tanggal_akhir_rs_jejaring: r.tanggal_akhir_rs_jejaring || "",
        });
        setDialogOpen(true);
    };

    const submit = async () => {
        if (!form.is_rs_jejaring && !form.departemen_id && !form.instalasi_stase_id) {
            return toast.error("Pilih Departemen atau Instalasi Stase, atau centang 'Rotasi ke RS Jejaring'");
        }
        if (form.is_rs_jejaring && !form.nama_rs_jejaring.trim()) {
            return toast.error("Nama RS Jejaring wajib diisi");
        }
        const pesanTanggalRsj = form.is_rs_jejaring
            ? pesanTanggalRsJejaring(form.tanggal_mulai_rs_jejaring, form.tanggal_akhir_rs_jejaring) : null;
        if (pesanTanggalRsj) return toast.error(pesanTanggalRsj);
        const pesanUrutan = (!form.is_rs_jejaring && pesanUrutanTanggal(form.tanggal_mulai_stase, form.tanggal_akhir_stase, "Stase"))
            || pesanUrutanTanggal(form.tanggal_mulai_jaga, form.tanggal_akhir_jaga, "Jaga");
        if (pesanUrutan) return toast.error(pesanUrutan);
        const lama = (k) => (editing ? [editing[`tanggal_mulai_${k}`] || "", editing[`tanggal_akhir_${k}`] || ""] : null);
        const pesanLintas = form.is_rs_jejaring
            ? pesanLintasBulanDialog(form.tanggal_mulai_rs_jejaring, form.tanggal_akhir_rs_jejaring, aturan, lama("rs_jejaring"))
            : pesanLintasBulanDialog(form.tanggal_mulai_stase, form.tanggal_akhir_stase, aturan, lama("stase"));
        if (pesanLintas) return toast.error(pesanLintas);
        const payload = {
            bulan: Number(form.bulan), tahun: Number(form.tahun),
            departemen_id: form.departemen_id || null, divisi_id: form.divisi_id || null,
            instalasi_stase_id: form.instalasi_stase_id || null,
            tanggal_mulai_stase: form.tanggal_mulai_stase || null, tanggal_akhir_stase: form.tanggal_akhir_stase || null,
            instalasi_jaga_id: form.instalasi_jaga_id || null,
            tanggal_mulai_jaga: form.tanggal_mulai_jaga || null, tanggal_akhir_jaga: form.tanggal_akhir_jaga || null,
            is_rs_jejaring: form.is_rs_jejaring, nama_rs_jejaring: form.nama_rs_jejaring || null,
            // "" (bukan null) supaya tanggal yg dikosongkan saat edit benar2 terhapus.
            tanggal_mulai_rs_jejaring: form.is_rs_jejaring ? form.tanggal_mulai_rs_jejaring : "",
            tanggal_akhir_rs_jejaring: form.is_rs_jejaring ? form.tanggal_akhir_rs_jejaring : "",
        };
        setSaving(true);
        try {
            if (editing) {
                await api.put(`/rotasi-klinik/${editing.id}`, payload);
                toast.success("Data rotasi diperbarui");
            } else {
                await api.post("/rotasi-klinik", { ...payload, peserta_id: pesertaId });
                toast.success("Rotasi klinik ditambahkan");
            }
            setDialogOpen(false);
            load();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    };

    const doDelete = async () => {
        if (!confirmDelete) return;
        try {
            await api.delete(`/rotasi-klinik/${confirmDelete.id}`);
            toast.success("Data rotasi dihapus");
            setConfirmDelete(null);
            load();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail));
        }
    };

    const describeTujuan = (r) => {
        if (r.is_rs_jejaring) return `RS Jejaring${r.nama_rs_jejaring ? `: ${r.nama_rs_jejaring}` : ""}`;
        const parts = [];
        if (r.departemen_id) parts.push(departemenMap[r.departemen_id]?.nama || "Departemen?");
        if (r.divisi_id) parts.push(divisiMap[r.divisi_id]?.nama || "Divisi?");
        if (r.instalasi_stase_id) parts.push(instalasiMap[r.instalasi_stase_id]?.nama || "Instalasi?");
        return parts.length ? parts.join(" — ") : "-";
    };

    // Baris "Jaga" di daftar segmen hanya tampil kalau salah satu field jaga terisi
    // (instalasi_jaga_id/tanggal_mulai_jaga/tanggal_akhir_jaga semuanya opsional).
    const hasJaga = (r) => !!(r.instalasi_jaga_id || r.tanggal_mulai_jaga || r.tanggal_akhir_jaga);
    const describeJaga = (r) => {
        const instalasi = r.instalasi_jaga_id ? (instalasiMap[r.instalasi_jaga_id]?.nama || "Instalasi?") : "-";
        return `${instalasi}, ${teksRentangIso(r.tanggal_mulai_jaga, r.tanggal_akhir_jaga)}`;
    };

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <p className="text-xs text-slate-500">
                    Rotasi klinis bulanan {pesertaNama || "peserta ini"} — murni monitoring, tidak memengaruhi Unit Cost.
                </p>
                <Button
                    size="sm" onClick={openAdd} disabled={locked}
                    className="bg-[#00B9AD] hover:bg-[#60C0D0]" data-testid="rotasi-add-button"
                >
                    <Plus className="w-4 h-4 mr-1.5" /> Tambah Rotasi
                </Button>
            </div>

            {loading ? (
                <div className="text-sm text-slate-400 py-6 text-center">Memuat...</div>
            ) : grouped.length === 0 ? (
                <div className="text-sm text-slate-400 py-6 text-center border border-dashed border-slate-200 rounded-lg">
                    Belum ada data rotasi klinik.
                </div>
            ) : (
                <div className="space-y-4">
                    {grouped.map((g) => (
                        <div key={`${g.tahun}-${g.bulan}`} className="border border-slate-200 rounded-lg overflow-hidden">
                            <div className="bg-slate-100 px-3 py-1.5 text-xs font-bold text-slate-700 flex items-center gap-1.5">
                                <Building2 className="w-3.5 h-3.5 text-slate-500" />
                                {BULAN_NAMA[g.bulan]} {g.tahun}
                                <Badge variant="outline" className="ml-1 text-[10px]">{g.items.length} segmen</Badge>
                            </div>
                            <table className="w-full compact">
                                <tbody>
                                    {g.items.map((r) => (
                                        <tr key={r.id} className="border-t border-slate-200 hover:bg-slate-50">
                                            <td className="text-slate-700 text-xs">
                                                <div className="font-semibold">{describeTujuan(r)}</div>
                                                <div className="text-slate-400">{teksTanggalSegmen(r)}</div>
                                                {hasJaga(r) && (
                                                    <div className="text-slate-400">Jaga: {describeJaga(r)}</div>
                                                )}
                                            </td>
                                            <td className="text-right pr-2 w-20">
                                                <Button size="sm" variant="ghost" disabled={locked} onClick={() => openEdit(r)} data-testid={`rotasi-edit-${r.id}`}>
                                                    <Pencil className="w-3.5 h-3.5" />
                                                </Button>
                                                <Button size="sm" variant="ghost" disabled={locked} className="text-rose-600 hover:text-rose-700" onClick={() => setConfirmDelete(r)} data-testid={`rotasi-delete-${r.id}`}>
                                                    <Trash2 className="w-3.5 h-3.5" />
                                                </Button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ))}
                </div>
            )}

            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>{editing ? "Edit Rotasi Klinik" : "Tambah Rotasi Klinik"}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label className="text-xs font-semibold uppercase text-slate-600">Bulan</Label>
                                <Select value={form.bulan} onValueChange={(v) => setForm({ ...form, bulan: v })}>
                                    <SelectTrigger data-testid="rotasi-form-bulan"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        {BULAN_NAMA.slice(1).map((b, i) => (
                                            <SelectItem key={i + 1} value={String(i + 1)}>{b}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label className="text-xs font-semibold uppercase text-slate-600">Tahun</Label>
                                <Input
                                    type="number" data-testid="rotasi-form-tahun"
                                    value={form.tahun} onChange={(e) => setForm({ ...form, tahun: e.target.value })}
                                />
                            </div>
                        </div>

                        <div className="flex items-center gap-2 pt-1">
                            <Checkbox
                                id="rs-jejaring" checked={form.is_rs_jejaring}
                                onCheckedChange={(v) => setForm({
                                    ...form, is_rs_jejaring: !!v,
                                    departemen_id: "", divisi_id: "",
                                    instalasi_stase_id: "", tanggal_mulai_stase: "", tanggal_akhir_stase: "",
                                    instalasi_jaga_id: "", tanggal_mulai_jaga: "", tanggal_akhir_jaga: "",
                                    tanggal_mulai_rs_jejaring: "", tanggal_akhir_rs_jejaring: "",
                                })}
                                data-testid="rotasi-form-rs-jejaring-checkbox"
                            />
                            <Label htmlFor="rs-jejaring" className="text-sm">Rotasi ke Rumah Sakit Jejaring (luar RSCM)</Label>
                        </div>

                        {form.is_rs_jejaring ? (
                            <>
                                <div>
                                    <Label className="text-xs font-semibold uppercase text-slate-600">Nama RS Jejaring</Label>
                                    <Input
                                        value={form.nama_rs_jejaring} data-testid="rotasi-form-nama-rs"
                                        onChange={(e) => setForm({ ...form, nama_rs_jejaring: e.target.value })}
                                        placeholder="mis. RSUD Pasar Rebo"
                                    />
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <Label className="text-xs font-semibold uppercase text-slate-600">Tanggal Mulai (opsional)</Label>
                                        <Input
                                            type="date" data-testid="rotasi-form-rsj-tanggal-mulai"
                                            value={form.tanggal_mulai_rs_jejaring}
                                            onChange={(e) => setForm({ ...form, tanggal_mulai_rs_jejaring: e.target.value })}
                                        />
                                    </div>
                                    <div>
                                        <Label className="text-xs font-semibold uppercase text-slate-600">Tanggal Akhir (opsional)</Label>
                                        <Input
                                            type="date" data-testid="rotasi-form-rsj-tanggal-akhir"
                                            value={form.tanggal_akhir_rs_jejaring}
                                            onChange={(e) => setForm({ ...form, tanggal_akhir_rs_jejaring: e.target.value })}
                                        />
                                    </div>
                                </div>
                                <p className="text-xs text-slate-500 -mt-1">
                                    Bila diisi, isi keduanya; Tanggal Mulai tidak boleh setelah Tanggal Akhir.
                                </p>
                            </>
                        ) : (
                            <>
                                <div>
                                    <Label className="text-xs font-semibold uppercase text-slate-600">Departemen</Label>
                                    <Select
                                        value={form.departemen_id || "__none__"}
                                        onValueChange={(v) => setForm({ ...form, departemen_id: v === "__none__" ? "" : v, divisi_id: "" })}
                                    >
                                        <SelectTrigger data-testid="rotasi-form-departemen"><SelectValue placeholder="Pilih departemen" /></SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="__none__">Tidak ada</SelectItem>
                                            {departemenList.map((d) => <SelectItem key={d.id} value={d.id}>{d.nama}</SelectItem>)}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div>
                                    <Label className="text-xs font-semibold uppercase text-slate-600">Divisi</Label>
                                    <Select
                                        value={form.divisi_id || "__none__"}
                                        onValueChange={(v) => setForm({ ...form, divisi_id: v === "__none__" ? "" : v })}
                                        disabled={!form.departemen_id || divisiForDepartemen.length === 0}
                                    >
                                        <SelectTrigger data-testid="rotasi-form-divisi">
                                            <SelectValue placeholder={!form.departemen_id ? "Pilih departemen dulu" : "Pilih divisi (opsional)"} />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="__none__">Tidak ada</SelectItem>
                                            {divisiForDepartemen.map((d) => <SelectItem key={d.id} value={d.id}>{d.nama}</SelectItem>)}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div>
                                    <Label className="text-xs font-semibold uppercase text-slate-600">Instalasi Stase</Label>
                                    <Select
                                        value={form.instalasi_stase_id || "__none__"}
                                        onValueChange={(v) => setForm({ ...form, instalasi_stase_id: v === "__none__" ? "" : v })}
                                    >
                                        <SelectTrigger data-testid="rotasi-form-instalasi"><SelectValue placeholder="Pilih instalasi (opsional)" /></SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="__none__">Tidak ada</SelectItem>
                                            {instalasiList.map((d) => <SelectItem key={d.id} value={d.id}>{d.nama}</SelectItem>)}
                                        </SelectContent>
                                    </Select>
                                </div>

                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <Label className="text-xs font-semibold uppercase text-slate-600">Tanggal Mulai Stase</Label>
                                        <Input
                                            type="date" data-testid="rotasi-form-tanggal-mulai"
                                            value={form.tanggal_mulai_stase} onChange={(e) => setForm({ ...form, tanggal_mulai_stase: e.target.value })}
                                        />
                                    </div>
                                    <div>
                                        <Label className="text-xs font-semibold uppercase text-slate-600">Tanggal Akhir Stase</Label>
                                        <Input
                                            type="date" data-testid="rotasi-form-tanggal-akhir"
                                            value={form.tanggal_akhir_stase} onChange={(e) => setForm({ ...form, tanggal_akhir_stase: e.target.value })}
                                        />
                                    </div>
                                </div>

                                <div>
                                    <Label className="text-xs font-semibold uppercase text-slate-600">Instalasi Jaga</Label>
                                    <Select
                                        value={form.instalasi_jaga_id || "__none__"}
                                        onValueChange={(v) => setForm({ ...form, instalasi_jaga_id: v === "__none__" ? "" : v })}
                                    >
                                        <SelectTrigger data-testid="rotasi-form-instalasi-jaga"><SelectValue placeholder="Pilih instalasi (opsional)" /></SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="__none__">Tidak ada</SelectItem>
                                            {instalasiList.map((d) => <SelectItem key={d.id} value={d.id}>{d.nama}</SelectItem>)}
                                        </SelectContent>
                                    </Select>
                                </div>

                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <Label className="text-xs font-semibold uppercase text-slate-600">Tanggal Mulai Jaga</Label>
                                        <Input
                                            type="date" data-testid="rotasi-form-tanggal-mulai-jaga"
                                            value={form.tanggal_mulai_jaga} onChange={(e) => setForm({ ...form, tanggal_mulai_jaga: e.target.value })}
                                        />
                                    </div>
                                    <div>
                                        <Label className="text-xs font-semibold uppercase text-slate-600">Tanggal Akhir Jaga</Label>
                                        <Input
                                            type="date" data-testid="rotasi-form-tanggal-akhir-jaga"
                                            value={form.tanggal_akhir_jaga} onChange={(e) => setForm({ ...form, tanggal_akhir_jaga: e.target.value })}
                                        />
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDialogOpen(false)}>Batal</Button>
                        <Button onClick={submit} disabled={saving} className="bg-[#00B9AD] hover:bg-[#60C0D0]" data-testid="rotasi-form-submit">
                            {saving ? "Menyimpan..." : "Simpan"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Hapus data rotasi ini?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Segmen rotasi {confirmDelete ? `${BULAN_NAMA[confirmDelete.bulan]} ${confirmDelete.tahun}` : ""} akan dihapus permanen.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Batal</AlertDialogCancel>
                        <AlertDialogAction className="bg-rose-600 hover:bg-rose-700" onClick={doDelete} data-testid="rotasi-delete-confirm">Hapus</AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
